import { addNotification } from "@/shared/states/notification";
import { Button, Input, Modal, ModalBody, ModalContent, cn } from "@nextui-org/react";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { createWhitelistedDomain } from "../../requests/whitelist";

interface AddDomainModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => Promise<void> | void;
}

const DOMAIN_REGEX =
  /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;

const isValidDomain = (value: string): boolean => {
  return DOMAIN_REGEX.test(value);
};

const normalizeDomain = (value: string) => {
  return value.trim().replace(/^@/, "").toLowerCase();
};

const getApiErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;

    if (status === 400) {
      return "Public email domains cannot be whitelisted. Please add a company domain.";
    }

    if (status === 409) {
      return "This domain is already associated with an organization.";
    }

    const data = error.response?.data as Record<string, unknown> | undefined;
    const apiError = data?.error_message;

    if (Array.isArray(apiError) && typeof apiError[0] === "string") {
      return apiError[0];
    }

    if (typeof apiError === "string" && apiError.trim()) {
      return apiError;
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Something went wrong. Please try again later.";
};

const AddDomainModal = ({ isOpen, onClose, onSuccess }: AddDomainModalProps) => {
  const [domain, setDomain] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const createDomainMutation = useMutation({
    mutationFn: createWhitelistedDomain,
  });

  const isSubmitting = createDomainMutation.isPending;

  useEffect(() => {
    if (!isOpen) return;
    setDomain("");
    setErrorMessage("");
  }, [isOpen]);

  const handleClose = () => {
    if (isSubmitting) return;
    setDomain("");
    setErrorMessage("");
    onClose();
  };

  const handleSubmit = async () => {
    const normalizedDomain = normalizeDomain(domain);
    if (!normalizedDomain) return;

    if (!isValidDomain(normalizedDomain)) {
      setErrorMessage("Please enter a valid domain (e.g., example.com).");
      return;
    }

    setErrorMessage("");

    try {
      await createDomainMutation.mutateAsync(normalizedDomain);
      await onSuccess();

      addNotification({
        type: "success",
        message: "Custom onboarding link generated successfully.",
      });

      onClose();
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      hideCloseButton={true}
      classNames={{
        base: "max-w-md",
        wrapper: "z-[9999] p-2",
      }}
    >
      <ModalContent>
        <ModalBody className="p-6">
          <div className="flex flex-col gap-6 w-full">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-zinc-900 text-center flex-grow">Add Domain</h3>
              <button
                type="button"
                onClick={handleClose}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                disabled={isSubmitting}
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            <p className="text-sm text-[#717179]">
              Enter the email domain for the external team to generate their unique access link.
            </p>

            <div className="w-full flex flex-col items-start gap-1.5">
              <label htmlFor="domain" className="text-sm font-normal text-[#18181B]">
                Domain
              </label>
              <Input
                id="domain"
                placeholder="e.g., example.com"
                value={domain}
                onChange={(e) => {
                  setDomain(e.target.value);
                  setErrorMessage("");
                }}
                className="w-full"
                classNames={{
                  inputWrapper: cn(
                    "bg-[#FAFAFA] border rounded-lg h-auto px-4 py-2",
                    errorMessage ? "border-[#F31260]" : "border-[#E4E4E7]",
                  ),
                  input: "text-sm placeholder:text-[#A1A1AA]",
                }}
                variant="flat"
                isInvalid={!!errorMessage}
                isDisabled={isSubmitting}
              />
              {errorMessage && <div className="text-[#F31260] text-xs mt-1 px-1">{errorMessage}</div>}
            </div>

            <div className="flex flex-col gap-3 pt-1">
              <div className="w-full border-t border-[#DCDCDC]" />
              <Button
                color="primary"
                onPress={handleSubmit}
                isDisabled={!normalizeDomain(domain) || isSubmitting}
                isLoading={isSubmitting}
                className="w-full h-9 text-sm font-medium bg-[#006FEE] text-white rounded-md"
              >
                Generate Access Link
              </Button>
            </div>
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AddDomainModal;
